import React, { useState } from 'react';
import styled from 'styled-components';

import { colors } from '../../../core';
import Heading from '../Heading';
import Icon from '../Icon';
import LoadingSpinner from '../LoadingSpinner';
import Modal from '../Modal';
import Text from '../Text';

export interface SectionProps {
  children?: React.ReactNode;
  loading?: boolean;
  onCloseClick?: () => void;
  onEditClick?: () => void;
  onHideClick?: () => void;
  onShowClick?: () => void;
  title: string;
  label?: string;
  disabled: boolean;
  complete?: boolean;
  viewState?: 'hide' | 'show' | 'edit' | 'editing';
  closeModalMessage?: string;
  displayMultipleButtons?: string[];
}

const Header = styled.div`
  border-bottom: 1px solid ${colors.primary};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
`;

const StyledHeading = styled(Heading).attrs({
  size: 6,
})<{ label: string }>`
  line-height: 26px;
  margin-bottom: 4px;
  ${({ label }) =>
    label &&
    `
    margin-left: 50px;
  `}
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-left: 12px;
  vertical-align: bottom-text;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
  margin-left: 20px;
`;

const Container = styled.section<{ disabled: boolean }>`
  margin-bottom: 30px;
  width: 100%;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.3;
  `}
`;

const MessageWrapper = styled.div`
  margin: 60px auto;
  max-width: 450px;
  text-align: center;
`;

const StyledLabel = styled.div`
  width: 34px;
  height: 34px;
  padding-top: 6px;
  position: absolute;
  left: 0px;
  bottom: 0px;
  background-color: ${colors.primary};
  color: ${colors.white};
  text-align: center;
`;

const HiddenContentMessage = () => (
  <MessageWrapper>
    <Heading size={7}>Confidential Information</Heading>
    <Text>
      If you need to see this section, click on the view icon at the top right of this panel.
    </Text>
  </MessageWrapper>
);

const Section = ({
  children,
  loading,
  onCloseClick,
  onEditClick,
  onHideClick,
  onShowClick,
  title,
  label,
  disabled,
  complete,
  viewState,
  closeModalMessage,
  displayMultipleButtons,
}: SectionProps) => {
  const [isShowingModal, setShowingModal] = useState(false);
  const showEditButton =
    onEditClick && (viewState === 'edit' || displayMultipleButtons?.includes('edit'));
  const showHideButton =
    onHideClick && (viewState === 'show' || displayMultipleButtons?.includes('show'));
  const showShowButton =
    onShowClick && (viewState === 'hide' || displayMultipleButtons?.includes('hide'));
  const showCloseButton = !!onCloseClick;

  const handleCloseClick = () => {
    if (onCloseClick) {
      if (closeModalMessage) {
        setShowingModal(true);
      } else {
        onCloseClick();
      }
    }
  };

  const handleEditClick = () => {
    if (onEditClick) {
      if (closeModalMessage && viewState === 'editing') {
        setShowingModal(true);
      } else {
        onEditClick();
      }
    }
  };

  const handleConfirm = () => {
    onEditClick && onEditClick();
    onCloseClick && onCloseClick();

    setShowingModal(false);
  };

  return (
    <React.Fragment>
      <Container disabled={disabled}>
        <Header>
          <StyledHeading label={label || ''}>
            {title}
            {loading && <StyledLoadingSpinner size="small" />}
          </StyledHeading>
          {label && <StyledLabel>{label}</StyledLabel>}
          {complete && <StyledIcon color={colors.white} type="complete" onClick={onShowClick} />}

          <Actions>
            {showShowButton && !disabled && (
              <StyledIcon color={colors.primary} type="view" onClick={onShowClick} />
            )}
            {showHideButton && !disabled && (
              <StyledIcon color={colors.primary} type="view-hide" onClick={onHideClick} />
            )}
            {showEditButton && !disabled && (
              <StyledIcon color={colors.primary} type="edit" onClick={handleEditClick} />
            )}
            {showCloseButton && !disabled && (
              <StyledIcon color={colors.primary} type="error" onClick={handleCloseClick} />
            )}
          </Actions>
        </Header>
        {viewState === 'hide' && <HiddenContentMessage />}
        {viewState !== 'hide' && children}
      </Container>
      {isShowingModal && (
        <Modal
          width="500px"
          title="Confirmation"
          showButtons
          onPress={handleConfirm}
          onClose={() => setShowingModal(false)}
        >
          {closeModalMessage}
        </Modal>
      )}
    </React.Fragment>
  );
};

Section.defaultProps = {
  loading: false,
  viewState: 'show',
  disabled: false,
};

export default Section;
